import React, { useState } from 'react';
import { Grid, IconButton, Typography } from '@mui/material';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { PasswordField } from './PasswordField';
import { AutoFixHigh } from '@mui/icons-material';
import generator from 'generate-password';
import { UseFormSetValue } from 'react-hook-form/dist/types/form';
import { PasswordValidationItem } from './PasswordField/component/CheckLine';

type FormPasswordProps = {
  control: any;
  required?: boolean;
  setValue: UseFormSetValue<any>;
  passwordLength?: number;
  validationConfig?: Array<PasswordValidationItem>;
};

export const FormPassword: React.FC<FormPasswordProps> = ({
  control,
  required = true,
  setValue,
  passwordLength = 64,
  validationConfig,
}) => {
  const { t } = useTranslation();
  const password = useWatch({ name: 'password', control });
  const [showPassword, setShowPassword] = useState(false);

  const generatePassword = () => {
    const newPassword = generator.generate({
      length: passwordLength,
      numbers: true,
      excludeSimilarCharacters: true,
      uppercase: true,
    });
    setValue('password', newPassword, { shouldDirty: true });
    setValue('confirmPassword', newPassword, { shouldDirty: true });
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} mt={4}>
        <Grid container spacing={2} alignItems={'center'}>
          <Grid item>
            <Typography variant={'h6'}>{t('password')}</Typography>
          </Grid>
          <Grid item>
            <IconButton onClick={generatePassword}>
              <AutoFixHigh />
            </IconButton>
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs={12} md={6}>
        <PasswordField
          label={t('password') + ' *'}
          controllerInitialState={{
            name: 'password',
            control,
            rules: required ? { required: t('requiredPassword') as string } : undefined,
            defaultValue: '',
          }}
          validationConfig={validationConfig}
          onPressShowPassword={show => setShowPassword(show)}
          showPassword={showPassword}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <PasswordField
          label={t('confirmPassword') + ' *'}
          controllerInitialState={{
            name: 'confirmPassword',
            control,
            rules: {
              required: required ? (t('requiredConfirmPassword') as string) : undefined,
              validate: confirmValue => confirmValue === password || (t('samePasswordError') as string),
            },
            defaultValue: '',
          }}
          validationConfig={[
            ...(validationConfig || []),
            { label: 'Password is identical', validation: confirmValue => confirmValue === password },
          ]}
          onPressShowPassword={show => setShowPassword(show)}
          showPassword={showPassword}
        />
      </Grid>
    </Grid>
  );
};
